import { createSlice, createAction, current, PayloadAction } from "@reduxjs/toolkit";
import { filter, findIndex, has, includes } from "lodash";

// Trigger action for saga compatibility (saga watches this)
export const fetchOrganizationPlanItems = createAction<any>(
  "organizationPlanItems/trigger"
);

// External action this slice responds to
import { logoutStart } from "@/store/slices/auth";

interface OrgPlanItemsEntry {
  loading: boolean;
  failed: boolean;
  items: any[];
}

interface OrganizationPlanItemsState {
  loading?: number;
  data: Record<number, OrgPlanItemsEntry>;
}

const initialState: OrganizationPlanItemsState = {
  data: {},
};

/**
 * Modifies plan item to omit properties we don't want in redux.
 */
const formatPlanItem = (planItem: any) => {
  const p = { ...planItem };
  delete p.items;
  delete p.response;
  return p;
};

/**
 * Ensures state has an entry for specified org.
 */
const initializeOrgEntry = (
  data: Record<number, OrgPlanItemsEntry>,
  orgId: number
) => {
  if (!has(data, orgId)) {
    data[Number(orgId)] = {
      loading: false,
      failed: false,
      items: [],
    };
  }
};

const organizationPlanItemsSlice = createSlice({
  name: "organizationPlanItems",
  initialState,
  reducers: {
    // Inject items into redux. (does not modify server data)
    // payload: { organization_id, items: [] }
    manuallyAddOrganizationPlanItems(state, action: PayloadAction<any>) {
      const { organization_id, items } = action.payload;
      initializeOrgEntry(state.data, organization_id);

      const draftItems = [...state.data[organization_id].items];

      for (let i = 0; i < items.length; i++) {
        const fpi = formatPlanItem(items[i]);
        const itemIndex = findIndex(draftItems, ["id", fpi.id]);

        if (-1 === itemIndex) {
          draftItems.push(fpi);
        } else {
          draftItems[itemIndex] = fpi;
        }
      }

      state.data[organization_id] = {
        loading: false,
        failed: false,
        items: draftItems,
      };
    },

    // Remove items from redux. (does not modify server data)
    // payload: { organization_id, items: [] }
    manuallyRemoveOrganizationPlanItems(state, action: PayloadAction<any>) {
      const { organization_id, items } = action.payload;
      initializeOrgEntry(state.data, organization_id);

      let draftItems = [...state.data[organization_id].items];

      const idsToRemove: number[] = [];
      for (let i = 0; i < items.length; i++) {
        idsToRemove.push(parseInt(items[i].id, 10));
      }
      draftItems = filter(draftItems, (item) => {
        return !includes(idsToRemove, item.id);
      });

      state.data[organization_id] = {
        loading: false,
        failed: false,
        items: draftItems,
      };
    },

    // Replaces one or more of an org's plan items in redux. (does not modify server data)
    // payload: { organization_id, items: [] }
    manuallyReplaceOrganizationPlanItems(state, action: PayloadAction<any>) {
      const { organization_id, items } = action.payload;
      initializeOrgEntry(state.data, organization_id);

      const draftItems = [...state.data[organization_id].items];

      for (let i = 0; i < draftItems.length; i++) {
        const thisId = parseInt(draftItems[i].id);
        for (let i2 = 0; i2 < items.length; i2++) {
          if (thisId === parseInt(items[i2].id, 10)) {
            draftItems[i] = items[i2];
          }
        }
      }

      state.data[organization_id] = {
        loading: false,
        failed: false,
        items: draftItems,
      };
    },

    // Removes all of org's plan items in redux. (does not modify server data)
    // payload: { organization_id }
    manuallyClearOrganizationPlanItems(state, action: PayloadAction<any>) {
      const { organization_id } = action.payload;
      initializeOrgEntry(state.data, organization_id);

      state.data[organization_id] = {
        loading: false,
        failed: false,
        items: [],
      };
    },

    // ------------------
    // Saga-based actions
    // ------------------
    fetchOrganizationPlanItemsStart(state, action: PayloadAction<any>) {
      // Add/increment loading flag (top-level, not org-specific).
      if (state.loading === undefined) {
        state.loading = 1;
      } else {
        state.loading++;
      }

      const orgId = Number(action.payload.organization_id);

      // Add org entry if not yet present.
      initializeOrgEntry(state.data, orgId);

      // Set-up the org specific data
      state.data[orgId].loading = true;
      state.data[orgId].failed = false;
      // preserve existing items
    },
    fetchOrganizationPlanItemsSuccess(state, action: PayloadAction<any>) {
      if (!action.payload?.data || !action.payload?.meta) {
        console.error(
          "Missing a payload property in fetchOrganizationPlanItemsSuccess.",
          action.payload
        );
        return;
      }

      // Extract org ID from the meta.path string.
      const path = action.payload.meta.path;
      const matches = path.match(
        /\/api\/v1\/organizations\/(\d+)\/plans\/current\/plan-items/
      );
      const orgId = Number(matches[1]);

      const planItems = action.payload.data.map((item: any) =>
        formatPlanItem(item)
      );

      state.loading = (state.loading || 1) - 1;
      state.data[orgId] = {
        loading: false,
        failed: false,
        items: planItems,
      };
    },
    fetchOrganizationPlanItemsFailure(state, action: PayloadAction<any>) {
      console.error("fetchOrganizationPlanItemsFailure", current(state), action.payload);
      state.loading = (state.loading || 1) - 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutStart, () => ({ ...initialState }));
  },
});

export const {
  manuallyAddOrganizationPlanItems,
  manuallyRemoveOrganizationPlanItems,
  manuallyReplaceOrganizationPlanItems,
  manuallyClearOrganizationPlanItems,
  fetchOrganizationPlanItemsStart,
  fetchOrganizationPlanItemsSuccess,
  fetchOrganizationPlanItemsFailure,
} = organizationPlanItemsSlice.actions;

export default organizationPlanItemsSlice.reducer;
